import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, Container } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

const bulletPoints = [
  'FSC-Certified Timber',
  'Stockless Trading Model',
  'Global Exporter',
];

const WhoWeAre = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const fadeInStyle = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : 'translateY(40px)',
    transition: 'opacity 1.1s cubic-bezier(.23,1.01,.32,1), transform 1.1s cubic-bezier(.23,1.01,.32,1)',
  };

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        aspectRatio: { xs: 'unset', sm: '16 / 9' },
        minHeight: { xs: '100vh', sm: 'auto' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        overflowX: 'hidden',
        my: { xs: 0, md: 0 },
        px: { xs: 0.5, sm: 0 }, // Add horizontal padding for mobile
      }}
    >
      {/* Background Image as <img> */}
      <Box
        component="img"
        src="/banner/banner2.jpg"
        alt="Who We Are Background"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          zIndex: 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          bgcolor: 'rgba(0,0,0,0.60)',
          zIndex: 2,
        }}
      />
      {/* Centered Card Content */}
      <Container
        sx={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: { xs: '100vh', sm: 'auto' },
          px: { xs: 0.5, sm: 0 },
        }}
      >
        <Box
          sx={{
            bgcolor: 'rgba(0,0,0,0.70)',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.25)',
            p: { xs: 1.2, sm: 2, md: 5 },
            maxWidth: { xs: 340, sm: 650 },
            width: '100%',
            mx: 'auto',
            textAlign: 'center',
            ...fadeInStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2.5rem', lg: '3rem' },
              mb: { xs: 1, sm: 2, md: 3 },
              color: '#fff',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              textShadow: '0 4px 24px rgba(0,0,0,0.7), 0 1px 2px rgba(0,0,0,0.7)',
            }}
          >
            Who We Are
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.92rem', sm: '1.05rem', md: '1.35rem' },
              mb: { xs: 1, sm: 2, md: 3 },
              color: '#fff',
              fontWeight: 500,
              textShadow: '0 2px 8px rgba(0,0,0,0.7)',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            DigiWood FZCO is a 100% export-oriented and FSC-certified trading company based in Dubai, UAE. Since our establishment in Dec-2024, we have been dedicated to sourcing high-quality veneer and plywood for global markets. Our mission is built on sustainability, efficiency, and a customer-centric approach.
          </Typography>
          <List dense sx={{ mb: { xs: 1, sm: 2, md: 3 }, maxWidth: 420, mx: 'auto' }}>
            {bulletPoints.map((point) => (
              <ListItem key={point} sx={{ pl: 0 }}>
                <ListItemIcon sx={{ minWidth: 32, color: 'success.main' }}>
                  <CheckCircleIcon fontSize="medium" />
                </ListItemIcon>
                <ListItemText primary={point} primaryTypographyProps={{ fontWeight: 700, color: '#fff', fontSize: { xs: '0.92rem', sm: '1.05rem', md: '1.15rem' }, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }} />
              </ListItem>
            ))}
          </List>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              navigate('/services');
              window.scrollTo(0, 0);
            }}
            sx={{
              fontWeight: 700,
              px: { xs: 2.2, sm: 3, md: 5 },
              py: { xs: 0.7, sm: 1, md: 1.3 },
              borderRadius: 2.5,
              letterSpacing: '0.04em',
              fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.18rem' },
              borderColor: '#fff',
              color: '#fff',
              background: 'rgba(0,0,0,0.08)',
              transition: 'all 0.25s',
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.10)',
              minWidth: { xs: 100, sm: 120, md: 160 },
              minHeight: { xs: 36, sm: 40, md: 48 },
              '&:hover': {
                background: '#fff',
                color: 'primary.main',
                borderColor: '#fff',
              },
            }}
          >
            Read More
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default WhoWeAre; 